import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import { createTheme, ThemeProvider, CssBaseline, Box, CircularProgress } from '@mui/material';
import { isMobile } from './deviceUtils';
import Residents from './components/Residents';
import Home from './components/Home';
import Payments from './components/Payments';
import MobilePayments from './components/MobilePayments';
import Invoices from './components/Invoices';
import Login from './components/Login';
import Sidebar from './components/Sidebar';
import { AuthProvider, useAuth } from './AuthContext';
import ProtectedRoute from './ProtectedRoute';
import Admin from './components/Admin';
import Terms from './components/Terms';
import Profiles from './components/Profiles';
import BillsPayable from './components/BillsPayable';
import Ledger from './components/Ledger';
import MobileLedger from './components/MobileLedger';

const theme = createTheme({
  typography: {
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    h4: {
      fontWeight: 600,
      letterSpacing: '-0.02em',
    },
    h5: {
      fontWeight: 500,
      letterSpacing: '-0.02em',
    },
    h6: {
      fontWeight: 500,
      letterSpacing: '-0.02em',
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
  },
  palette: {
    mode: 'dark',
    primary: {
      main: '#FFFFFF',
    },
    secondary: {
      main: '#A1A1AA',
    },
    accent: {
      main: '#2d3748',
      light: '#3a4a61',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#0a0a0b',
      paper: '#1a1a1b9e',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#A1A1AA',
    },
    success: {
      main: '#22C55E',
      soft: 'rgba(34, 197, 94, 0.1)',
    },
    warning: {
      main: '#F59E0B',
      soft: 'rgba(245, 158, 11, 0.1)',
    },
    error: {
      main: '#EF4444',
      soft: 'rgba(239, 68, 68, 0.1)',
    },
    info: {
      main: '#3B82F6',
      soft: 'rgba(59, 130, 246, 0.1)',
    },
    grey: {
      50: '#FAFAFA',
      100: '#F4F4F5',
      200: '#E4E4E7',
      300: '#D4D4D8',
      400: '#A1A1AA',
      500: '#71717A',
      600: '#52525B',
      700: '#3F3F46',
      800: '#27272A',
      900: '#18181B',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          padding: '10px 20px',
          backgroundColor: '#1a1a1b9e',
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: '#3F3F46',
          },
          '&.MuiButton-contained': {
            color: '#FFFFFF',
            '&.Mui-disabled': {
              color: 'rgba(255, 255, 255, 0.5)',
              backgroundColor: '#3F3F46',
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          backgroundColor: '#1a1a1b9e',
          borderRadius: 16,
          border: '1px solid #3F3F46',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          border: 'none',
          backgroundColor: '#0a0a0b',
          borderRight: '1px solid #1a1a1b9e',
          width: 280,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: '#0a0a0b',
            borderRadius: 12,
            '& fieldset': {
              borderColor: '#3F3F46',
            },
            '&:hover fieldset': {
              borderColor: '#52525B',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#71717A',
            },
            '& input:-webkit-autofill': {
              '-webkit-box-shadow': '0 0 0 100px #1a1a1b inset',
              '-webkit-text-fill-color': '#FFFFFF',
              'caret-color': '#FFFFFF',
            },
          },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            textTransform: 'uppercase',
            fontSize: '0.75rem',
            fontWeight: 600,
            letterSpacing: '0.05em',
            color: '#A1A1AA',
            borderBottom: '1px solid #3F3F46',
            padding: '12px 16px',
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.03)',
            },
          },
          '& .MuiTableCell-root': {
            padding: '12px 16px',
            borderBottom: '1px solid rgba(63, 63, 70, 0.5)',
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1a1a1b',
          border: '1px solid #3F3F46',
          borderRadius: 16,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #3F3F46',
          padding: '20px 24px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          borderTop: '1px solid #3F3F46',
          padding: '16px 24px',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        standardInfo: {
          backgroundColor: 'rgba(59, 130, 246, 0.06)',
          color: '#A1A1AA',
          border: '1px solid rgba(59, 130, 246, 0.12)',
          '& .MuiAlert-icon': {
            color: '#3B82F6',
            opacity: 0.7,
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: '#1a1a1b',
          border: '1px solid #3F3F46',
          borderRadius: 12,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1a1a1b9e',
          color: '#FFFFFF',
          border: '1px solid #3F3F46',
          borderRadius: 8,
          fontSize: '0.875rem',
          padding: '8px 12px',
        },
        arrow: {
          color: '#1a1a1b9e',
        },
        popper: {
          transition: 'none !important',
        },
      },
      defaultProps: {
        arrow: true,
        placement: 'top',
        enterDelay: 100,
        leaveDelay: 0,
        TransitionProps: { timeout: 150 },
      },
    },
  },
});

function App() {
  return (
    <AuthProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Layout />
        </Router>
      </ThemeProvider>
    </AuthProvider>
  );
}

const Layout = () => {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [typedKeys, setTypedKeys] = useState('');

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Enter' && typedKeys === 'admin' && isAdmin) {
        navigate('/admin');
      } else {
        setTypedKeys((prev) => (prev + event.key).slice(-5));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [typedKeys, isAdmin, navigate]);

  if (loading) {
    return (
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: 'background.default',
        }}
      >
        <CircularProgress sx={{ color: '#FFFFFF' }} />
      </Box>
    );
  }

  if (!user) {
    return (
      <Box component="main" sx={{ flexGrow: 1 }}>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'background.default' }}>
      {isMobile() ? (
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
          <Routes>
            <Route path="/profiles" element={<Profiles />} />
            <Route path="/mobileledger" element={<MobileLedger />} />
            <Route path="/mobilepayments" element={<MobilePayments />} />
            <Route path="*" element={<Navigate to="/profiles" />} />
          </Routes>
        </Box>
      ) : (
        <>
          <Sidebar />
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              p: 3,
              width: `calc(100% - 280px)`,
              transition: 'width 0.2s ease-out',
            }}
          >
            <Routes>
              <Route path="/login" element={user ? <Navigate replace to="/" /> : <Login />} />
              <Route path="/" element={<ProtectedRoute><Home /></ProtectedRoute>} />
              <Route path="/residents" element={<ProtectedRoute><Residents /></ProtectedRoute>} />
              <Route path="/payments" element={<ProtectedRoute><Payments /></ProtectedRoute>} />
              <Route path="/invoices" element={<ProtectedRoute><Invoices /></ProtectedRoute>} />
              <Route path="/bills-payable" element={<ProtectedRoute><BillsPayable /></ProtectedRoute>} />
              <Route path="/ledger" element={<ProtectedRoute><Ledger /></ProtectedRoute>} />
              <Route path="/admin" element={isAdmin ? <Admin /> : <Navigate to="/" />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Box>
        </>
      )}
    </Box>
  );
};

export default App;
